import React, { useState } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import PasswordInput from "./PasswordInput";
import PasswordStrength from "./PasswordStrength";
import { KeyRound, ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Self-contained change-password dialog. Works for any authenticated
// password-based account (respondents + doctors). Renders three password
// fields (current + new + confirm), each with its own show/hide eye toggle,
// and calls POST /api/auth/change-password. On success the dialog closes and
// resets so a subsequent open starts fresh.
export default function ChangePasswordDialog({ open, onOpenChange, trigger }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); };
  const handleOpenChange = (o) => { onOpenChange(o); if (!o) reset(); };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from your current password");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound size={16} /> Change your password
          </DialogTitle>
          <DialogDescription>
            Confirm your current password, then choose a new one (at least 8 characters).
            All existing sessions on other devices stay signed in until they expire.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="cp-current">Current password</Label>
            <PasswordInput id="cp-current" required autoComplete="current-password"
                           value={current} onChange={(e) => setCurrent(e.target.value)}
                           data-testid="change-password-current" />
          </div>
          <div>
            <Label htmlFor="cp-new">New password</Label>
            <PasswordInput id="cp-new" required autoComplete="new-password" minLength={8}
                           value={next} onChange={(e) => setNext(e.target.value)}
                           data-testid="change-password-new" />
            <PasswordStrength value={next} testId="change-password-strength" />
          </div>
          <div>
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <PasswordInput id="cp-confirm" required autoComplete="new-password" minLength={8}
                           value={confirm} onChange={(e) => setConfirm(e.target.value)}
                           data-testid="change-password-confirm" />
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}
                    data-testid="change-password-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}
                    className="rounded-full mano-lift"
                    data-testid="change-password-submit">
              <ShieldCheck size={14} /> {submitting ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
